import { useState } from 'react';
import { Modal } from 'antd';
import { ShoppingItemStatus } from '../../../../types/shopping';
import { useIsMounted } from '../../../../utils/hooks';

interface ExtractionStatusProps {
  prepareData: (showPendingItems: boolean) => Promise<void>;
  shoppingCartData: any;
  onCheckout: () => void;
}

export default function useExtractionStatus({ prepareData, shoppingCartData, onCheckout }: ExtractionStatusProps) {
  const ALLOWED_EXTRACTION_TIME = 300000; // 5 minutes
  const isMounted = useIsMounted();
  
  const [pendingExtractions, setPendingExtractions] = useState<boolean>(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  // Returns a Promise that resolves after 'ms' Milliseconds
  const timer = (ms: number) => new Promise((res) => setTimeout(res, ms));

  async function checkExtractionStatus(elapsedTime: number = 0) {
    if (!isMounted) return;

    try {
      setLoadingCheckout(true);
      
      if (elapsedTime >= ALLOWED_EXTRACTION_TIME) {
        // When full extraction times out we should inform the user
        Modal.info({
          title:
            'We are sorry, the data preparation is still pending. Please remove the pending items from the cart and Proceed to Checkout again.',
          content: null,
          centered: true,
          onOk() { },
        });
        setPendingExtractions(false);
        return;
      }

      if (!shoppingCartData) {
        return;
      }

      const result: ShoppingItemStatus[] = [];

      Object.keys(shoppingCartData.shoppingItems).forEach((pointName: string) => {
        const itemsPerPoint = shoppingCartData.shoppingItems[pointName];
        Object.keys(itemsPerPoint).forEach((category: string) => {
          const item = itemsPerPoint[category];
          result.push({
            pointName: item.point_name,
            orderId: item.order_id,
            category: item.dataset_category,
            extractionStatus: item.extractionStatus,
          });
        });
      });

      if (isMounted && result.length === 0) {
        Modal.info({
          title:
            'Your shopping cart is empty. Please select at least one item before checkout.',
          content: null,
          centered: true,
          onOk() { },
        });
      } else {
        await prepareData(true);
        
        const issues = result.filter((item: ShoppingItemStatus) => {
          return item.extractionStatus === 'Error';
        });
        
        if (issues.length > 0) {
          Modal.info({
            title:
              'We are sorry, the data preparation failed. Please remove the failed items from the cart and Proceed to Checkout again.',
            content: null,
            centered: true,
            onOk() { },
          });
          setPendingExtractions(false);
        } else {
          const pending = result.filter((item: ShoppingItemStatus) => {
            return item.extractionStatus === 'Pending';
          });
          
          if (pending.length > 0) {
            setPendingExtractions(true);
            await timer(3000);
            await checkExtractionStatus(elapsedTime + 3000);
          } else {
            setPendingExtractions(false);
            isMounted && onCheckout();
          }
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingCheckout(false);
    }
  }

  return {
    pendingExtractions,
    loadingCheckout,
    checkExtractionStatus
  };
}
